import React from 'react';
import { renderToString } from 'react-dom/server';
import { useNavigate, useParams } from 'react-router-dom';

import { troubleshootingHoldOption } from '@constants/troubleshootingHoldOption';

import { getAIStatusesList } from '@helpers/getAIStatusesList';

import useTranslation from '@hooks/useTranslation';

import { useActionItemsStorePersistAction } from '@pages/ActionItems/store/useActionItemsStorePersist';

import ActionItemsLegend from '../components/ActionItemsStatuses/ActionItemsLegend';
import style from '../components/ActionItemsStatuses/style.module.scss';
import { needResponseStatuses, pendingUpdatesStatuses } from '../constants/aiStatuses';
import type {
  IActionItemsStatusesCount,
  IActionItemsStatusesPoint,
  IActionItemsStatusesVisible,
  TActionItemsStatusesKey,
} from '../model/actionItemsStatuses';

export const useGetActionItemsStatusesOptions = (
  needResponseTotal: number,
  pendingUpdatesTotal: number,
  needResponse: Record<TActionItemsStatusesKey, IActionItemsStatusesCount>,
  pendingUpdates: Record<TActionItemsStatusesKey, IActionItemsStatusesCount>,
  seriesData: Highcharts.PointOptionsObject[],
  setAiStatusesVisible: React.Dispatch<React.SetStateAction<IActionItemsStatusesVisible>>,
) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { customerId } = useParams();

  const { setAIStatusType } = useActionItemsStorePersistAction();

  const statusesList = getAIStatusesList(t);

  const aiByTypeOptions: Highcharts.Options = {
    chart: { height: 155 },
    title: { text: '' },
    credits: { enabled: false },
    legend: {
      enabled: true,
      symbolWidth: 0,
      symbolHeight: 0,
      align: 'center',
      useHTML: true,
      events: {
        itemClick: function (event) {
          const { name } = event.legendItem as Highcharts.Point;
          setAiStatusesVisible((prev) => ({
            ...prev,
            [name]: !prev[name as keyof IActionItemsStatusesVisible],
          }));
        },
      },
      labelFormatter: function () {
        const { name, color } = this;
        const isNeedResponse = name === 'Need response';
        return renderToString(
          <ActionItemsLegend
            name={name}
            color={color as string}
            data={
              isNeedResponse
                ? {
                    statuses: needResponseStatuses,
                    statusValues: needResponse,
                    total: needResponseTotal,
                  }
                : {
                    statuses: pendingUpdatesStatuses,
                    statusValues: pendingUpdates,
                    total: pendingUpdatesTotal,
                  }
            }
          />,
        );
      },
    },
    tooltip: {
      formatter: function () {
        const { point, y } = this;
        return renderToString(
          <>
            {t(point.series.name)}: <b>{y}</b>
          </>,
        );
      },
    },
    plotOptions: {
      bar: {
        stacking: 'normal',
        events: {
          hide: function () {
            this.update({
              type: 'bar',
              states: {
                hover: {
                  enabled: false,
                },
              },
            });
          },
        },
        dataLabels: {
          enabled: true,
          y: 20,
          align: 'right',
          verticalAlign: 'bottom',
          className: style.barLabel,
        },
      },
    },
    yAxis: {
      visible: false,
      reversedStacks: false,
      stackLabels: { enabled: false },
      endOnTick: false,
      maxPadding: 0,
    },
    xAxis: { visible: false },
    accessibility: { enabled: false },
    series: [
      { type: 'bar', color: '#3C97AA', name: 'Need response', data: [{ y: needResponseTotal }] },
      { type: 'bar', color: '#D0D3DC', name: 'Pending Update', data: [{ y: pendingUpdatesTotal }] },
    ],
  };

  const aiByStatusOptions: Highcharts.Options = {
    title: {
      text: '',
    },
    legend: { enabled: false },
    credits: { enabled: false },
    tooltip: {
      format: '{point.name}: <b>{point.y}</b>',
    },
    plotOptions: {
      series: {
        dataLabels: [
          {
            padding: 0,
            enabled: true,
            formatter: function () {
              const { point, y } = this;
              if (y === 0) {
                return null;
              }
              return renderToString(
                <>
                  <div className={style.labelValue}>{y}</div>
                  <div className={style.labelStatus}>{t(point.name)}</div>
                </>,
              );
            },
            useHTML: true,
          },
        ],
      },
      pie: {
        states: {
          hover: {
            opacity: 1,
            brightness: 0,
            borderWidth: 1,
            borderColor: 'grey',
          },
        },
        point: {
          events: {
            click: function () {
              const { custom } = this as IActionItemsStatusesPoint;
              const option = statusesList.find((status) =>
                custom.key === 'troubleshootingHold'
                  ? status.value === troubleshootingHoldOption.id
                  : status.value === custom.key,
              );
              setAIStatusType(option);
              navigate(`/customer/${customerId}`);
            },
          },
        },
      },
    },
    accessibility: { enabled: false },
    series: [
      {
        type: 'pie',
        name: '',
        slicedOffset: 0,
        data: seriesData,
      },
    ],
  };

  return { aiByTypeOptions, aiByStatusOptions };
};
